import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { QRCodeSVG } from 'qrcode.react';
import {
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';

/**
 * AuthenticatorSetup is a standalone page/tab accessible after login.
 * Users can set up or manage their TOTP authenticator app here.
 * Styled to match the login flow (ForgotPassword / NewPasswordSetup).
 */
function AuthenticatorSetup() {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Status
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [preferredMfa, setPreferredMfa] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Setup flow
  const [showSetup, setShowSetup] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    checkCurrentStatus();
  }, []);

  const checkCurrentStatus = async () => {
    setLoading(true);
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const email = user.attributes?.email || user.username;
      setUserEmail(email);

      const mfa = await Auth.getPreferredMFA(user);
      setPreferredMfa(mfa);
      setTotpEnabled(mfa === 'SOFTWARE_TOKEN_MFA');
    } catch (error) {
      console.error('Error checking MFA status:', error);
      setErrorMessage('Unable to load MFA status.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setShowSetup(true);
    setLoading(true);

    try {
      const user = await Auth.currentAuthenticatedUser();
      const secret = await Auth.setupTOTP(user);
      setTotpSecret(secret);

      const issuer = 'DatacomDataCentres';
      const otpauthUrl = `otpauth://totp/${issuer}:${userEmail}?secret=${secret}&issuer=${issuer}`;
      setQrCodeUrl(otpauthUrl);
    } catch (error) {
      console.error('TOTP setup error:', error);
      setErrorMessage(error.message || 'Failed to initiate authenticator setup.');
      setShowSetup(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setVerifying(true);

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, totpCode);
      await Auth.setPreferredMFA(user, 'TOTP');

      setTotpEnabled(true);
      setPreferredMfa('SOFTWARE_TOKEN_MFA');
      setShowSetup(false);
      setTotpSecret('');
      setQrCodeUrl('');
      setTotpCode('');
      setSuccessMessage(
        'Authenticator app configured successfully! Your next login will use authenticator codes instead of SMS.'
      );
    } catch (error) {
      console.error('TOTP verify error:', error);
      if (error.code === 'EnableSoftwareTokenMFAException') {
        setErrorMessage(
          'Invalid code. Please wait for a new code in your authenticator app and try again.'
        );
      } else {
        setErrorMessage(error.message || 'Verification failed. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSwitchToSms = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setVerifying(true);

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.setPreferredMFA(user, 'SMS');

      setTotpEnabled(false);
      setPreferredMfa('SMS_MFA');
      setSuccessMessage('Switched to SMS-based MFA. Your next login will use SMS codes.');
    } catch (error) {
      console.error('Switch to SMS error:', error);
      setErrorMessage(error.message || 'Failed to switch MFA method.');
    } finally {
      setVerifying(false);
    }
  };

  const handleCancelSetup = () => {
    setShowSetup(false);
    setTotpSecret('');
    setQrCodeUrl('');
    setTotpCode('');
    setErrorMessage('');
  };

  if (loading && !showSetup) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <Typography variant="overline" color="text.primary">
        Authenticator App
      </Typography>
      <Typography variant="body2" color="text.primary" style={{ opacity: 0.7 }}>
        Use an authenticator app (e.g. Google Authenticator, Microsoft Authenticator) for more secure
        login verification.
      </Typography>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      {/* Current Status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <Typography variant="body2" color="text.primary">Current method:</Typography>
        {totpEnabled ? (
          <Chip label="Authenticator App" color="lightsuccess" size="small" />
        ) : (
          <Chip label="SMS" color="accent3" size="small" />
        )}
      </div>

      {/* Setup or Manage */}
      {!showSetup ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {!totpEnabled ? (
            <Button variant="contained" color="primary" onClick={handleStartSetup} size="large">
              Set Up Authenticator App
            </Button>
          ) : (
            <>
              <Button variant="outlined" color="primary" onClick={handleStartSetup} size="large">
                Reconfigure Authenticator
              </Button>
              <Button variant="outlined" color="warning" onClick={handleSwitchToSms} size="large">
                Switch Back to SMS
              </Button>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <Typography variant="body2" color="text.primary" style={{ textAlign: 'center' }}>
            1. Open your authenticator app and scan this QR code.
          </Typography>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center' }}>
                <QRCodeSVG
                  value={qrCodeUrl}
                  size={180}
                />
              </div>

              <Typography variant="body2" color="text.primary" style={{ textAlign: 'center', opacity: 0.7 }}>
                Or enter this key manually:
              </Typography>
              <div
                style={{
                  padding: '8px 12px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  textAlign: 'center',
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: 'rgba(128,128,128,0.3)',
                }}
              >
                {totpSecret}
              </div>

              <Typography variant="body2" color="text.primary" style={{ textAlign: 'center' }}>
                2. Enter the 6-digit code from your app to verify.
              </Typography>

              <form onSubmit={handleVerifyTotp} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <TextField
                  label="Authenticator Code"
                  variant="outlined"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  required
                  disabled={verifying}
                  inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={verifying}
                  size="large"
                >
                  {verifying ? <CircularProgress size={24} /> : 'Verify & Enable'}
                </Button>
                <Button onClick={handleCancelSetup} color="inherit" size="small">
                  Cancel
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AuthenticatorSetup;
